import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface Banner {
  id: number;
  title: string;
  image_url: string;
  link_url: string;
  position: number;
  is_active: boolean;
  created_at: string;
}

export default function AdminBannersPage() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editBanner, setEditBanner] = useState<Banner | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [title, setTitle] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [position, setPosition] = useState("0");
  const [previewUrl, setPreviewUrl] = useState("");

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .order("position", { ascending: true });

      if (error) throw error;
      setBanners(data || []);
    } catch {
      setMessage({ type: "error", text: "배너 목록을 불러오지 못했습니다" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const resetForm = useCallback(() => {
    setTitle("");
    setImageUrl("");
    setLinkUrl("");
    setPosition("0");
    setPreviewUrl("");
    setEditBanner(null);
    setShowForm(false);
  }, []);

  const openCreateForm = useCallback(() => {
    resetForm();
    setShowForm(true);
  }, [resetForm]);

  const openEditForm = useCallback((banner: Banner) => {
    setEditBanner(banner);
    setTitle(banner.title);
    setImageUrl(banner.image_url);
    setLinkUrl(banner.link_url || "");
    setPosition(String(banner.position));
    setPreviewUrl(banner.image_url);
    setShowForm(true);
  }, []);

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "파일 크기는 5MB 이하여야 합니다" });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `banners/${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("admin-assets")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("admin-assets").getPublicUrl(fileName);
      const publicUrl = urlData.publicUrl;

      setImageUrl(publicUrl);
      setPreviewUrl(publicUrl);
      setMessage({ type: "success", text: "이미지 업로드 완료!" });
    } catch {
      setMessage({ type: "error", text: "이미지 업로드에 실패했습니다" });
    } finally {
      setUploading(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!title.trim() || !imageUrl.trim()) {
      setMessage({ type: "error", text: "제목과 이미지를 입력해주세요" });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        title: title.trim(),
        image_url: imageUrl,
        link_url: linkUrl.trim() || null,
        position: parseInt(position, 10) || 0,
      };

      if (editBanner) {
        const { error } = await supabase
          .from("banners")
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq("id", editBanner.id);
        if (error) throw error;
        setMessage({ type: "success", text: "배너가 수정되었습니다" });
      } else {
        const { error } = await supabase.from("banners").insert(payload);
        if (error) throw error;
        setMessage({ type: "success", text: "배너가 등록되었습니다" });
      }

      resetForm();
      fetchBanners();
    } catch {
      setMessage({ type: "error", text: "저장에 실패했습니다" });
    } finally {
      setSaving(false);
    }
  }, [title, imageUrl, linkUrl, position, editBanner, resetForm, fetchBanners]);

  const handleToggleActive = useCallback(
    async (banner: Banner) => {
      try {
        const { error } = await supabase
          .from("banners")
          .update({ is_active: !banner.is_active, updated_at: new Date().toISOString() })
          .eq("id", banner.id);
        if (error) throw error;

        setBanners((prev) =>
          prev.map((b) => (b.id === banner.id ? { ...b, is_active: !b.is_active } : b)),
        );
      } catch {
        setMessage({ type: "error", text: "상태 변경에 실패했습니다" });
      }
    },
    [],
  );

  const handleDelete = useCallback(async (banner: Banner) => {
    try {
      const { error } = await supabase.from("banners").delete().eq("id", banner.id);
      if (error) throw error;

      setBanners((prev) => prev.filter((b) => b.id !== banner.id));
      setMessage({ type: "success", text: "배너가 삭제되었습니다" });
    } catch {
      setMessage({ type: "error", text: "삭제에 실패했습니다" });
    }
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-foreground-800">배너 관리</h2>
          <p className="text-xs text-foreground-600 mt-0.5">총 {banners.length}개</p>
        </div>

        <button
          onClick={showForm ? resetForm : openCreateForm}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
            showForm
              ? "bg-background-200 text-foreground-600 hover:bg-background-300"
              : "bg-primary-500 text-foreground-50 hover:bg-primary-600"
          }`}
        >
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={showForm ? "ri-close-line" : "ri-add-line"} />
          </span>
          {showForm ? "취소" : "배너 추가"}
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-xs font-medium ${
            message.type === "success"
              ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h3 className="text-sm font-bold text-foreground-800 mb-4">
            {editBanner ? "배너 수정" : "새 배너 등록"}
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left: form fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">제목</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="배너 제목"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">이미지</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-background-200 text-foreground-600 text-xs font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-upload-cloud-2-line" />
                    </span>
                    {uploading ? "업로드 중..." : "이미지 업로드"}
                  </button>
                  <span className="text-[10px] text-foreground-600 self-center">또는 URL 직접 입력</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setPreviewUrl(e.target.value);
                  }}
                  placeholder="이미지 URL"
                  className="w-full mt-2 bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">링크 URL (선택)</label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="배너 클릭 시 이동할 URL"
                  className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">순서</label>
                <input
                  type="number"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  min="0"
                  className="w-24 bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-2.5 bg-primary-500 text-foreground-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {saving ? "저장 중..." : editBanner ? "수정하기" : "등록하기"}
              </button>
            </div>

            {/* Right: preview */}
            <div className="bg-background-200 rounded-lg border border-background-300 p-3 flex flex-col">
              <span className="text-[10px] text-foreground-600 mb-2">미리보기</span>
              {previewUrl ? (
                <div className="flex-1 rounded-lg overflow-hidden bg-background-100 min-h-[180px] flex items-center justify-center">
                  <img
                    src={previewUrl}
                    alt={title || "배너 미리보기"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              ) : (
                <div className="flex-1 rounded-lg bg-background-100 min-h-[180px] flex items-center justify-center">
                  <span className="text-xs text-foreground-600">이미지를 업로드하거나 URL을 입력하세요</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Banner List */}
      <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-image-line text-xl text-foreground-600" />
            </div>
            <p className="text-sm text-foreground-600">등록된 배너가 없습니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-300 bg-background-200/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">이미지</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">제목</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden md:table-cell">링크</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">순서</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">상태</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">관리</th>
                </tr>
              </thead>
              <tbody>
                {banners.map((banner) => (
                  <tr
                    key={banner.id}
                    className="border-b border-background-300 last:border-0 hover:bg-background-200/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="w-20 h-12 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={banner.image_url}
                          alt={banner.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{banner.title}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-[10px] text-foreground-600 max-w-[200px] truncate block">
                        {banner.link_url || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs text-foreground-600">{banner.position}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleActive(banner)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          banner.is_active
                            ? "bg-accent-100/80 text-accent-800"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className={banner.is_active ? "ri-eye-line" : "ri-eye-off-line"} />
                        </span>
                        {banner.is_active ? "활성" : "비활성"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditForm(banner)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-background-200 hover:bg-background-300 text-foreground-600 transition-colors cursor-pointer"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-edit-line" />
                          </span>
                        </button>
                        <button
                          onClick={() => handleDelete(banner)}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors cursor-pointer"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-delete-bin-line" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}